import { useQuery } from "@tanstack/react-query";
import { PlatformLayout } from "./platform-layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Users, Download } from "lucide-react";
import { format } from "date-fns";

interface WaitlistEntry {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  createdAt: string;
}

function exportCsv(rows: WaitlistEntry[]) {
  const header = ["Name", "Email", "Phone", "Signed Up"];
  const lines = rows.map((r) => [
    `"${r.name.replace(/"/g, '""')}"`,
    `"${r.email.replace(/"/g, '""')}"`,
    `"${(r.phone ?? "").replace(/"/g, '""')}"`,
    `"${format(new Date(r.createdAt), "yyyy-MM-dd HH:mm:ss")}"`,
  ].join(","));
  const csv = [header.join(","), ...lines].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `waitlist-${format(new Date(), "yyyy-MM-dd")}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export function WaitlistContent() {
  const { data: entries = [], isLoading } = useQuery<WaitlistEntry[]>({
    queryKey: ["/api/platform/waitlist"],
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Waitlist</h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            People who signed up to be notified when Clarik launches.
          </p>
        </div>
        <Button
          onClick={() => exportCsv(entries)}
          disabled={entries.length === 0}
          data-testid="button-export-csv"
          size="sm"
          className="gap-2"
        >
          <Download className="h-4 w-4" />
          Export CSV
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-gray-400" />
            <CardTitle className="text-base">
              {isLoading ? "Loading…" : `${entries.length} submission${entries.length !== 1 ? "s" : ""}`}
            </CardTitle>
          </div>
          <CardDescription>Sorted newest first.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-16 text-gray-400 text-sm">
              Loading…
            </div>
          ) : entries.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400 gap-2">
              <Users className="h-8 w-8 opacity-40" />
              <span className="text-sm">No waitlist submissions yet.</span>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Signed Up</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map((entry) => (
                  <TableRow key={entry.id} data-testid={`row-waitlist-${entry.id}`}>
                    <TableCell className="font-medium" data-testid={`text-name-${entry.id}`}>
                      {entry.name}
                    </TableCell>
                    <TableCell data-testid={`text-email-${entry.id}`}>
                      {entry.email}
                    </TableCell>
                    <TableCell className="text-gray-500" data-testid={`text-phone-${entry.id}`}>
                      {entry.phone ?? "—"}
                    </TableCell>
                    <TableCell className="text-gray-500 text-sm" data-testid={`text-date-${entry.id}`}>
                      {format(new Date(entry.createdAt), "MMM d, yyyy h:mm a")}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default function PlatformWaitlist() {
  return <PlatformLayout><WaitlistContent /></PlatformLayout>;
}
